import React from 'react'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Divider, Stack, Typography, useTheme } from '@mui/material'
import { ChargingCardModel } from '../api/models/charging-card-model'
import { useTableStyles } from '../../shared/styles/table.styles'
import { J1DownarrowD } from '@e3dc-react/icons'
import { ChargingCardsTableMenuIcon } from './ChargingCardsTableMenuIcon'
import { ChargingCardStatusChip } from '../../shared/components/ChargingCardStatusChip'
import { LinkedWallboxesCell } from './LinkedWallboxesCell'
import useModals from 'src/Hooks/useModals'
import { format } from '@e3dc-react/shell/Libs/date'

interface ChargingCardsMobileTableRowItemProps {
  chargingCard: ChargingCardModel
  installationId: number
  isManagedMode: boolean
  installationHasWallboxes: boolean
}

/**
 * Component is visible on mobile and tablet screen/view.
 * It is single row inside charging cards table for smaller screens
 * @param chargingCard - ChargingCardModel object
 * @param installationId - unique identifier of installation as number
 */
export const ChargingCardsMobileTableRowItem: React.FC<ChargingCardsMobileTableRowItemProps> = ({
  chargingCard,
  installationId,
  isManagedMode,
  installationHasWallboxes,
}) => {
  const { t } = useCustomTranslation()
  const { classes } = useTableStyles()
  const theme = useTheme()
  const { openChargingCardDetailsDialog } = useModals()

  return (
    <Accordion elevation={0} className={classes.accordion}>
      <AccordionSummary className={classes.accordionSummary} expandIcon={<J1DownarrowD />}>
        <Stack flexDirection={'row'} justifyContent={'space-between'} alignItems={'center'} sx={{ width: '100%', ml: 1 }}>
          <Stack>
            <Typography variant="body2" color={theme.palette.grey.grey03}>
              {chargingCard.tokenId}
            </Typography>
            <Typography variant="body2">{chargingCard.cardName}</Typography>
          </Stack>
          <ChargingCardsTableMenuIcon chargingCard={chargingCard} installationId={installationId} />
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <Stack className={classes.accordionDetailsContainer}>
          <Typography variant="body2">{t('emobility.chargingCards.edit.user')}</Typography>
          <Typography variant="body2">{chargingCard.user || '-'}</Typography>
        </Stack>
        <Divider className={classes.divider} />
        <Stack className={classes.accordionDetailsContainer}>
          <Typography variant="body2">{t('emobility.chargingCards.status.status')}</Typography>
          <ChargingCardStatusChip status={chargingCard.status} />
        </Stack>
        <Divider className={classes.divider} />
        <Stack className={classes.accordionDetailsContainer}>
          <Typography variant="body2">{t('emobility.chargingCards.created')}</Typography>
          <Typography variant="body2">{format(new Date(chargingCard.createdAt), t('common.format.date-fns-date'))}</Typography>
        </Stack>
        <Divider className={classes.divider} />
        <Stack className={classes.accordionDetailsContainer}>
          <Typography variant="body2">{t('emobility.chargingCards.changed')}</Typography>
          <Typography variant="body2">{format(new Date(chargingCard.changedAt), t('common.format.date-fns-date'))}</Typography>
        </Stack>
        <Divider className={classes.divider} />
        <Stack className={classes.accordionDetailsContainer}>
          <Typography variant="body2">{t('emobility.chargingCards.works-on')}</Typography>

          <Box ml={4} sx={{ wordBreak: 'break-word' }}>
            <LinkedWallboxesCell
              installationId={installationId}
              linkedWallboxes={chargingCard.linkedWallboxes}
              chargingCard={chargingCard}
              isManagedMode={isManagedMode}
            />
          </Box>
        </Stack>

        <Stack direction="row" paddingY={1}>
          <Button
            variant="text"
            size="medium"
            onClick={() => openChargingCardDetailsDialog({ chargingCard, installationId, isManagedMode, installationHasWallboxes })}
          >
            {t('emobility.chargingSession.show-session-details-button-text')}
          </Button>
        </Stack>
      </AccordionDetails>
    </Accordion>
  )
}
